import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
  Link,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../store/slices/authSlice';
import {
  confirmTwoFactor,
  getPendingTwoFactorToken,
  redirectPathForLoginResponse,
  setupTwoFactor,
  verifyTwoFactorLogin,
} from '../services/twoFactorService';
import BrandMark from '../components/BrandMark';
import { palette, FONT_DISPLAY, FONT_MONO } from '../theme';

const TwoFactorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') === 'setup' ? 'setup' : 'verify';
  const isAdmin = searchParams.get('admin') === 'true';
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [setupStarted, setSetupStarted] = useState(false);
  const [pendingLoginResponse, setPendingLoginResponse] = useState<any>(null);

  const finishLogin = useCallback((response: any) => {
    if (response.user && response.token) {
      dispatch(loginSuccess({ user: response.user, token: response.token, agency: response.agency }));
      navigate(redirectPathForLoginResponse(response, isAdmin));
    }
  }, [dispatch, navigate, isAdmin]);

  useEffect(() => {
    if (!getPendingTwoFactorToken()) {
      navigate(isAdmin ? '/admin/login' : '/login');
      return;
    }

    if (mode === 'setup' && !setupStarted) {
      setSetupStarted(true);
      setupTwoFactor()
        .then((data) => {
          setQrCodeDataUrl(data.qrCodeDataUrl);
          setManualEntryKey(data.manualEntryKey);
        })
        .catch((err) => {
          setError(err.response?.data?.error || err.message || 'Failed to start setup');
        });
    }
  }, [mode, navigate, isAdmin, setupStarted]);

  const handleVerifyLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await verifyTwoFactorLogin(code, useBackupCode ? backupCode : undefined);
      finishLogin(response);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await confirmTwoFactor(code);
      if (response.backupCodes?.length) {
        setBackupCodes(response.backupCodes);
        if (response.token) setPendingLoginResponse(response);
      } else if (response.token) {
        finishLogin(response);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodesAck = () => {
    if (pendingLoginResponse) {
      finishLogin(pendingLoginResponse);
    }
  };

  if (backupCodes) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={700}>
            Save your backup codes
          </Typography>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Store these codes somewhere safe. Each code can be used once if you lose access to your authenticator app.
          </Alert>
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 3 }}>
            {backupCodes.map((c) => (
              <Typography key={c} sx={{ letterSpacing: 2 }}>{c}</Typography>
            ))}
          </Box>
          <Button variant="contained" fullWidth onClick={handleBackupCodesAck}>
            I have saved my backup codes
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: palette.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Container maxWidth="xs">
        <Stack direction="row" gap={1.5} alignItems="center" sx={{ mb: 4, justifyContent: 'center' }}>
          <BrandMark size={32} />
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 16 }}>Workflow.Estate</Typography>
        </Stack>

        <Paper sx={{ p: 4 }}>
          <Typography component="h1" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 24, mb: 1 }}>
            {mode === 'setup' ? 'Set up authenticator' : 'Two-factor verification'}
          </Typography>
          <Typography sx={{ color: palette.inkMuted, mb: 3, fontSize: 14 }}>
            {mode === 'setup'
              ? 'Administrator accounts require an authenticator app. Scan the QR code with Google Authenticator, Microsoft Authenticator, Authy, or similar.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {mode === 'setup' && qrCodeDataUrl && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img src={qrCodeDataUrl} alt="Authenticator QR code" style={{ maxWidth: 200 }} />
              <Typography sx={{ mt: 2, fontFamily: FONT_MONO, fontSize: 12, wordBreak: 'break-all' }}>
                Manual key: {manualEntryKey}
              </Typography>
            </Box>
          )}

          <Box component="form" onSubmit={mode === 'setup' ? handleConfirmSetup : handleVerifyLogin}>
            {!useBackupCode ? (
              <TextField
                fullWidth
                label="Authentication code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                autoFocus
                required
                sx={{ mb: 2 }}
              />
            ) : (
              <TextField
                fullWidth
                label="Backup code"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                autoFocus
                required
                sx={{ mb: 2 }}
              />
            )}

            <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ mb: 2 }}>
              {loading ? 'Verifying…' : mode === 'setup' ? 'Enable & continue' : 'Verify'}
            </Button>

            {mode === 'verify' && (
              <Button
                variant="text"
                fullWidth
                onClick={() => setUseBackupCode((v) => !v)}
                sx={{ mb: 1 }}
              >
                {useBackupCode ? 'Use authenticator code instead' : 'Use a backup code instead'}
              </Button>
            )}

            <Link component={RouterLink} to={isAdmin ? '/admin/login' : '/login'} variant="body2" sx={{ display: 'block', textAlign: 'center' }}>
              Back to sign in
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TwoFactorPage;
